import { useEffect, useRef, useState } from 'react';

interface Props {
  heading: string;
  subheading: string;
  embedSrc: string;
  areaList?: string[];
}

export default function ServiceMap({ heading, subheading, embedSrc, areaList }: Props) {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 bg-[#0D1E35] relative overflow-hidden">
      {/* Decorative gradient blobs */}
      <div className="absolute top-0 left-0 w-96 h-96 rounded-full opacity-5 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #D4AF37 0%, transparent 70%)', transform: 'translate(-30%, -30%)' }} />
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full opacity-5 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #D4AF37 0%, transparent 70%)', transform: 'translate(30%, 30%)' }} />

      <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
        {/* Heading */}
        <div
          className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 mb-5">
            <i className="ri-map-pin-2-fill text-[#D4AF37] text-sm" />
            <span className="text-[#D4AF37] text-xs font-semibold tracking-widest uppercase">Service Area</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4"
            style={{ fontFamily: "'Montserrat', sans-serif" }}>
            {heading.split('Sleaford').map((part, i, arr) => (
              i < arr.length - 1 ? (
                <span key={i}>
                  {part}
                  <span
                    style={{
                      background: 'linear-gradient(135deg, #D4AF37, #F5E27A, #D4AF37)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >Sleaford</span>
                </span>
              ) : <span key={i}>{part}</span>
            ))}
          </h2>
          <p className="text-white/60 text-base max-w-2xl mx-auto">{subheading}</p>
        </div>

        {/* Map + Areas grid */}
        <div className={`grid grid-cols-1 lg:grid-cols-3 gap-8 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Map iframe */}
          <div className="lg:col-span-2 rounded-2xl overflow-hidden border border-white/10 relative" style={{ height: '460px' }}>
            <div className="absolute inset-0 z-10 pointer-events-none rounded-2xl"
              style={{ boxShadow: 'inset 0 0 0 2px rgba(212,175,55,0.2)' }} />
            <iframe
              title="Electrical Installations Sleaford service area map"
              src={embedSrc}
              width="100%"
              height="100%"
              style={{ border: 0, filter: 'invert(0.9) hue-rotate(180deg) saturate(0.8) brightness(0.85)' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          {/* Service area list */}
          <div className="flex flex-col gap-4">
            {/* Call to action card */}
            <div className="rounded-2xl p-6 border border-[#D4AF37]/30 flex flex-col gap-3"
              style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.12) 0%, rgba(212,175,55,0.04) 100%)' }}>
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#D4AF37]/20">
                <i className="ri-map-pin-2-fill text-[#D4AF37] text-xl" />
              </div>
              <h3 className="text-white font-bold text-lg" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Based in Sleaford
              </h3>
              <p className="text-white/60 text-sm leading-relaxed">
                Our electricians are based in Sleaford, Lincolnshire — offering fast response times and local knowledge across the whole area.
              </p>
              <a href="tel:01529265060"
                className="inline-flex items-center gap-2 text-[#D4AF37] font-semibold text-sm mt-1 whitespace-nowrap hover:opacity-80 transition-opacity"
              >
                <i className="ri-phone-fill" />
                01529 265060
              </a>
            </div>

            {/* Areas covered */}
            {areaList && areaList.length > 0 && (
              <div className="rounded-2xl p-6 border border-white/10 bg-white/5 flex-1">
                <p className="text-white/50 text-xs font-semibold tracking-widest uppercase mb-4">Areas Covered</p>
                <div className="flex flex-wrap gap-2">
                  {areaList.map((area) => (
                    <span key={area}
                      className="text-xs px-3 py-1 rounded-full border border-[#D4AF37]/25 text-[#D4AF37]/80 bg-[#D4AF37]/5">
                      {area}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
